/**
 * PriceTable — таблицы суб-таба «Цены» (screen-18): QuestPriceTable
 * (renderQTab js:111-135) и ServicePriceTable (renderVTab js:170-192).
 *
 * Квесты: при фильтре «ВСЕ» — группы-строки филиалов .bhead; у квеста со
 * спецверсиями — маркер .ovrdot.qdot; double-tap 320мс по названию —
 * подстроки спецверсий (.sub, ↳); long-press 350мс по названию — drag
 * БЛОКОВ строк (useRowDrag, A40) в пределах филиала, порядок персональный.
 * Услуги (A41): сетевые строки без филиалов; double-tap — подстроки ВСЕХ
 * филиалов (.sub.svb) с ovrCell-семантикой: .inh = наследование сетевой,
 * правка = override, long-press по своему значению — «Сбросить к сетевой»
 * (PopMenu), .ovrdot у услуги с override. Название ячейки-драга несёт
 * data-si — арбитраж с pull-to-refresh (A43), touch-action:none в CSS.
 * Ячейки открывают NumPadPop хоста (openPop), сейвы — usePricesAdmin (A38).
 */
import { Fragment, useRef } from "react";
import type { Branch, MasterClass, Quest } from "../../api/types";
import { FLAB, KQ, type PricesAdmin } from "../../hooks/usePricesAdmin";
import { useRowDrag } from "../../hooks/useRowDrag";
import { cellPop, PriceCell, type PopCfg } from "./PriceRow";
import css from "./prices.module.css";

/** Короткая метка филиала капсом (renderBsw / BranchTabs.branchLabel). */
const bLab = (b: Branch): string => (b.name?.trim() ?? b.id).toUpperCase();

/** Double-tap 320мс (js:122-125) — на строку, по ключу id. */
function useDoubleTap() {
  const t = useRef<Record<string, number>>({});
  return (id: string, cb: () => void) => {
    const now = Date.now();
    if (now - (t.current[id] ?? 0) < 320) cb();
    t.current[id] = now;
  };
}

const head = (first: string) => (
  <thead><tr>
    <th>{first}</th><th>До, чел</th><th>База, ₽</th><th>+₽/чел</th>
    <th className={css.mn}>Мин, ₽</th>
  </tr></thead>
);

const rowDragCfg = {
  blockOf: (main: HTMLElement) => {
    const rows = [main];
    let n = main.nextElementSibling;
    while (n instanceof HTMLElement && n.dataset.sub !== undefined) { rows.push(n); n = n.nextElementSibling; }
    return rows;
  },
  dragClass: css.dragrow!,
  settleClass: css.settling!,
};

export interface QuestTableProps {
  branches: Branch[];
  grouped: boolean;
  questsOf: (branchId: string) => Quest[];
  pa: PricesAdmin;
  qOpen: ReadonlySet<string>;
  onToggle: (questId: string) => void;
  onReorder: (branchId: string, ids: string[]) => void;
  openPop: (cfg: PopCfg) => void;
}

export function QuestPriceTable({ branches, grouped, questsOf, pa, qOpen, onToggle, onReorder, openPop }: QuestTableProps) {
  const tap = useDoubleTap();
  const dragB = useRef("");
  const drag = useRowDrag({
    ...rowDragCfg,
    mainOf: (cell) => {
      const tr = cell.closest<HTMLElement>("tr[data-qid]");
      dragB.current = tr?.dataset.b ?? "";
      return tr;
    },
    mains: (main) => Array.from(
      main.closest("tbody")?.querySelectorAll<HTMLElement>(`tr[data-qid][data-b="${main.dataset.b ?? ""}"]`) ?? []),
    idOf: (m) => m.dataset.qid,
    onReorder: (ids) => onReorder(dragB.current, ids),
  });
  return (
    <table className={css.ptab}>
      {head("Квест")}
      <tbody>
        {branches.map((b) => (
          <Fragment key={b.id}>
            {grouped ? (
              <tr className={css.bhead}><td className={`${css.qn} ${css.bh}`}>{bLab(b)}</td><td colSpan={4} /></tr>
            ) : null}
            {questsOf(b.id).map((q) => {
              const specs = pa.versionsOf(q.id);
              return (
                <Fragment key={q.id}>
                  <tr data-qid={q.id} data-b={b.id}>
                    <td
                      className={css.qn} data-si=""
                      onPointerDown={drag.onPointerDown}
                      onPointerUp={() => { if (!drag.dragged()) tap(q.id, () => { if (specs.length) onToggle(q.id); }); }}
                    >
                      {q.name}{specs.length ? <span className={`${css.ovrdot} ${css.qdot}`} /> : null}
                    </td>
                    {KQ.map((f) => {
                      const k = `q:${q.id}:${f}`;
                      const val = pa.qPrice(q.id)[f];
                      return (
                        <td key={f}>
                          <PriceCell
                            key={pa.flash?.key === k ? `s${pa.flash.n}` : "c"}
                            val={val} mn={f === "mn"} saved={pa.flash?.key === k}
                            onOpen={(a) => openPop(cellPop(q.name, f, val, a, (v) => pa.saveQuest(q.id, f, v)))}
                          />
                        </td>
                      );
                    })}
                  </tr>
                  {qOpen.has(q.id) ? specs.map((sv) => (
                    <tr key={sv.id} data-sub="" className={css.sub}>
                      <td className={css.qn}>{sv.title}</td>
                      {KQ.map((f) => {
                        const k = `s:${sv.id}:${f}`;
                        const val = pa.vPrice(sv)[f];
                        return (
                          <td key={f}>
                            <PriceCell
                              key={pa.flash?.key === k ? `s${pa.flash.n}` : "c"}
                              val={val} mn={f === "mn"} saved={pa.flash?.key === k}
                              onOpen={(a) => openPop(cellPop(sv.title, f, val, a, (v) => pa.saveVersion(sv.id, f, v)))}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  )) : null}
                </Fragment>
              );
            })}
          </Fragment>
        ))}
      </tbody>
    </table>
  );
}

export interface ServiceTableProps {
  services: MasterClass[];
  branches: Branch[];
  pa: PricesAdmin;
  sOpen: ReadonlySet<string>;
  onToggle: (id: string) => void;
  onReorder: (ids: string[]) => void;
  openPop: (cfg: PopCfg) => void;
  /** Хостовое меню «Сбросить к сетевой» (один PopMenu на панель). */
  openMenu: (anchor: HTMLElement, onPick: () => void) => void;
}

export function ServicePriceTable({ services, branches, pa, sOpen, onToggle, onReorder, openPop, openMenu }: ServiceTableProps) {
  const tap = useDoubleTap();
  const drag = useRowDrag({
    ...rowDragCfg,
    mainOf: (cell) => cell.closest<HTMLElement>("tr[data-sid]"),
    mains: (main) => Array.from(main.closest("tbody")?.querySelectorAll<HTMLElement>("tr[data-sid]") ?? []),
    idOf: (m) => m.dataset.sid,
    onReorder,
  });
  return (
    <table className={css.ptab}>
      {head("Услуга")}
      <tbody>
        {services.map((m) => (
          <Fragment key={m.id}>
            <tr data-sid={m.id}>
              <td
                className={css.qn} data-si=""
                onPointerDown={drag.onPointerDown}
                onPointerUp={() => { if (!drag.dragged()) tap(m.id, () => onToggle(m.id)); }}
              >
                <span className={css.mkn}>{m.name}</span>
                {pa.hasOvr(m.id) ? <span className={css.ovrdot} /> : null}
              </td>
              {KQ.map((f) => {
                const k = `v:${m.id}:${f}`;
                const val = pa.sPrice(m.id)[f];
                return (
                  <td key={f}>
                    <PriceCell
                      key={pa.flash?.key === k ? `s${pa.flash.n}` : "c"}
                      val={val} mn={f === "mn"} saved={pa.flash?.key === k}
                      onOpen={(a) => openPop(cellPop(m.name, f, val, a, (v) => pa.saveService(m.id, f, v)))}
                    />
                  </td>
                );
              })}
            </tr>
            {sOpen.has(m.id) ? branches.map((b) => (
              <tr key={b.id} data-sub="" className={`${css.sub} ${css.svb}`}>
                <td className={css.qn}>{bLab(b)}</td>
                {KQ.map((f) => {
                  const o = pa.ovr(m.id, b.id, f);
                  const val = o ?? pa.sPrice(m.id)[f];
                  const k = `o:${m.id}:${b.id}:${f}`;
                  return (
                    <td key={f}>
                      <PriceCell
                        key={pa.flash?.key === k ? `s${pa.flash.n}` : "c"}
                        val={val} mn={f === "mn"} inh={o == null} saved={pa.flash?.key === k}
                        onOpen={(a) => openPop({
                          ...cellPop(m.name, f, val, a, (v) => pa.saveOvr(m.id, b.id, f, v)),
                          /* эталон openOvrCell js:151-154: имя до 14 + филиал */
                          label: `${m.name.slice(0, 14)} · ${bLab(b)} · ${FLAB[f]}`,
                        })}
                        onLp={o != null ? (a) => openMenu(a, () => pa.resetOvr(m.id, b.id, f)) : undefined}
                      />
                    </td>
                  );
                })}
              </tr>
            )) : null}
          </Fragment>
        ))}
      </tbody>
    </table>
  );
}
